import React, { useState, useEffect } from 'react';
import { SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions, ScrollView, RefreshControl, StatusBar, Alert, Modal, FlatList, ActivityIndicator } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const { width } = Dimensions.get('window');
const PRIMARY = '#0e4e96';
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token')||await AsyncStorage.getItem('token'); if(t)c.headers.Authorization=`Bearer ${t}`; }catch{} return c; });
const EP = { companyProfile:(id)=>`/api/company-profile/${id}`, deals:'/api/data/crm_deals', contacts:'/api/data/crm_contacts', employees:'/api/employees', activities:'/api/data/crm_activities' };
const getBusinessId=async()=>{try{return await AsyncStorage.getItem('company_id')||await AsyncStorage.getItem('company_id')||'0';}catch{return '0';}};
const safeData=r=>(r?.data?.success&&Array.isArray(r.data.data))?r.data.data:[];

export default function LeadAssignmentScreen({ navigation, route }) {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('assignments');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [unassignedLeads, setUnassignedLeads] = useState([]);
  const [assignedLeads, setAssignedLeads] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assignmentStats, setAssignmentStats] = useState({ total:0, assigned:0, unassigned:0, avgPerRep:0 });

  useEffect(()=>{init();},[]);
  const init=async()=>{try{setIsLoading(true);const b=route?.params?.company_id||await getBusinessId();setCompanyId(b);await fetchAll(b);}catch(e){console.error(e);}finally{setIsLoading(false);}};
  const onRefresh=async()=>{setRefreshing(true);await init();setRefreshing(false);};

  const fetchAll=async(bizId)=>{
    const cid=parseInt(bizId)||0;const p={company_id:cid};
    const [profileRes,dealsRes,employeesRes]=await Promise.allSettled([
      api.get(EP.companyProfile(cid)),api.get(EP.deals,{params:p}),api.get(EP.employees,{params:p}),
    ]);
    const profile=profileRes.status==='fulfilled'?(profileRes.value?.data?.success?(Array.isArray(profileRes.value.data.data)?profileRes.value.data.data[0]:profileRes.value.data.data):null):null;
    if(profile){setCompanyProfile(profile);await AsyncStorage.setItem('company_profile',JSON.stringify(profile));}
    else{try{const c=await AsyncStorage.getItem('company_profile');if(c)setCompanyProfile(JSON.parse(c));}catch{}}

    const deals=safeData(dealsRes.status==='fulfilled'?dealsRes.value:null);
    const emps=safeData(employeesRes.status==='fulfilled'?employeesRes.value:null);

    const salesTeam=emps.filter(e=>(e.department||'').toLowerCase().includes('sales')||(e.position||'').toLowerCase().includes('sales')||(e.position||'').toLowerCase().includes('account'));
    const team=(salesTeam.length>0?salesTeam:emps.slice(0,6)).map(e=>{
      const name=`${e.first_name||''} ${e.last_name||''}`.trim()||`Employee ${e.id}`;
      const assigned=deals.filter(d=>String(d.assigned_to)===String(e.id));
      return { id:String(e.id), name, department:e.department||'Sales', position:e.position||'Sales Rep',
        email:e.email||'', assignedCount:assigned.length,
        totalValue:assigned.reduce((s2,d)=>s2+parseFloat(d.value||0),0),
        capacity:10, available:10-assigned.length };
    });
    setTeamMembers(team);

    const unassigned=deals.filter(d=>!d.assigned_to).map(d=>({
      id:String(d.id), name:d.contact_name||'Unknown', company:d.company||'',
      email:d.contact_email||'', value:parseFloat(d.value||0), stage:d.stage||'lead',
      source:d.source||'direct', priority:d.priority||'medium', createdAt:d.created_at, dealId:d.id,
    }));
    const assigned=deals.filter(d=>d.assigned_to).map(d=>{
      const rep=team.find(t=>String(t.id)===String(d.assigned_to));
      return { id:String(d.id), name:d.contact_name||'Unknown', company:d.company||'',
        email:d.contact_email||'', value:parseFloat(d.value||0), stage:d.stage||'lead',
        assignedTo:rep?.name||`User ${d.assigned_to}`, assignedToId:d.assigned_to, createdAt:d.created_at, dealId:d.id };
    });
    setUnassignedLeads(unassigned);
    setAssignedLeads(assigned);
    setAssignmentStats({
      total:deals.length, assigned:assigned.length, unassigned:unassigned.length,
      avgPerRep:team.length>0?Math.round(assigned.length/team.length):0,
    });
  };

  const assignLead=async(lead,memberId)=>{
    try{
      await api.put(`${EP.deals}/${lead.dealId}`,{assigned_to:parseInt(memberId)});
      Alert.alert('Assigned',`Lead assigned successfully`);
      setShowAssignModal(false); setSelectedLead(null);
      await fetchAll(companyId);
    }catch(e){Alert.alert('Error',e.message);}
  };

  const autoAssign=async()=>{
    if(unassignedLeads.length===0){Alert.alert('Info','No unassigned leads');return;}
    if(teamMembers.length===0){Alert.alert('Info','No team members available');return;}
    Alert.alert('Auto-Assign',`Assign ${unassignedLeads.length} leads to ${teamMembers.length} team members using round-robin?`,[
      {text:'Cancel',style:'cancel'},
      {text:'Assign',onPress:async()=>{
        try{
          for(let i=0;i<unassignedLeads.length;i++){
            const member=teamMembers[i%teamMembers.length];
            await api.put(`${EP.deals}/${unassignedLeads[i].dealId}`,{assigned_to:parseInt(member.id)});
          }
          Alert.alert('Done',`${unassignedLeads.length} leads assigned`);
          await fetchAll(companyId);
        }catch(e){Alert.alert('Error',e.message);}
      }},
    ]);
  };

  const formatCurrency=v=>new Intl.NumberFormat('en-GB',{style:'currency',currency:companyProfile?.currency||'GBP',minimumFractionDigits:0,maximumFractionDigits:0}).format(v||0);
  const formatDate=d=>d?new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short'}):'-';

  const renderHeader=()=>(
    <LinearGradient colors={['#B45309','#D97706']} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity onPress={()=>navigation?.goBack()} style={st.headerBtn}><MaterialCommunityIcons name="arrow-left" size={24} color="#FFF"/></TouchableOpacity>
        <View style={st.headerCenter}>
          <Text style={st.headerTitle}>{companyProfile?.name||'Lead Assignment'}</Text>
          <Text style={st.headerSub}>{assignmentStats.unassigned} unassigned · {assignmentStats.assigned} assigned</Text>
        </View>
        <TouchableOpacity onPress={autoAssign} style={st.headerBtn}><MaterialCommunityIcons name="auto-fix" size={24} color="#FFF"/></TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderStats=()=>(
    <View style={st.statsRow}>
      {[{l:'Total',v:assignmentStats.total,c:'#B45309'},{l:'Assigned',v:assignmentStats.assigned,c:'#10B981'},{l:'Unassigned',v:assignmentStats.unassigned,c:'#EF4444'},{l:'Avg/Rep',v:assignmentStats.avgPerRep,c:'#3B82F6'}].map((s2,i)=>(
        <View key={i} style={st.statCard}><Text style={[st.statValue,{color:s2.c}]}>{s2.v}</Text><Text style={st.statLabel}>{s2.l}</Text></View>
      ))}
    </View>
  );

  const renderTabs=()=>(
    <View style={st.tabsRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:12}}>
      {[{k:'assignments',t:'Unassigned',i:'account-alert'},{k:'assigned',t:'Assigned',i:'account-check'},{k:'team',t:'Team Workload',i:'account-group'}].map(tab=>(
        <TouchableOpacity key={tab.k} style={[st.tab,activeTab===tab.k&&st.activeTab]} onPress={()=>setActiveTab(tab.k)}>
          <MaterialCommunityIcons name={tab.i} size={18} color={activeTab===tab.k?'#B45309':'#6B7280'}/>
          <Text style={[st.tabText,activeTab===tab.k&&{color:'#B45309',fontWeight:'600'}]}>{tab.t}</Text>
          {tab.k==='assignments'&&unassignedLeads.length>0&&<View style={st.tabBadge}><Text style={st.tabBadgeText}>{unassignedLeads.length}</Text></View>}
        </TouchableOpacity>
      ))}
    </ScrollView></View>
  );

  const renderUnassigned=()=>unassignedLeads.length===0?(
    <View style={st.empty}><MaterialCommunityIcons name="check-circle" size={48} color="#10B981"/><Text style={st.emptyText}>All leads are assigned!</Text></View>
  ):(
    <View style={{padding:16}}>{unassignedLeads.map((l,i)=>(
      <View key={i} style={st.leadCard}>
        <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'flex-start'}}>
          <View style={{flex:1}}>
            <Text style={st.leadName}>{l.name}</Text>
            <Text style={st.leadMeta}>{l.company} · {l.source} · {l.stage}</Text>
            {l.value>0&&<Text style={st.leadValue}>{formatCurrency(l.value)}</Text>}
          </View>
          <TouchableOpacity style={st.assignBtn} onPress={()=>{setSelectedLead(l);setShowAssignModal(true);}}>
            <MaterialCommunityIcons name="account-plus" size={18} color="#B45309"/><Text style={st.assignBtnText}>Assign</Text>
          </TouchableOpacity>
        </View>
      </View>
    ))}</View>
  );

  const renderAssigned=()=>assignedLeads.length===0?(
    <View style={st.empty}><MaterialCommunityIcons name="account-alert" size={48} color="#D1D5DB"/><Text style={st.emptyText}>No assigned leads yet</Text></View>
  ):(
    <View style={{padding:16}}>{assignedLeads.map((l,i)=>(
      <View key={i} style={st.leadCard}>
        <View style={{flexDirection:'row',justifyContent:'space-between'}}>
          <View style={{flex:1}}><Text style={st.leadName}>{l.name}</Text><Text style={st.leadMeta}>{l.company} · {l.stage}</Text></View>
          <View style={{alignItems:'flex-end'}}>{l.value>0&&<Text style={st.leadValue}>{formatCurrency(l.value)}</Text>}
            <View style={st.assignedBadge}><MaterialCommunityIcons name="account" size={12} color="#10B981"/><Text style={st.assignedName}>{l.assignedTo}</Text></View></View>
        </View>
      </View>
    ))}</View>
  );

  const renderTeam=()=>(
    <View style={st.section}><Text style={st.sectionTitle}>Team Workload</Text>
      {teamMembers.length===0?<View style={st.empty}><MaterialCommunityIcons name="account-group-outline" size={48} color="#D1D5DB"/><Text style={st.emptyText}>No team members</Text></View>:
        teamMembers.map((m,i)=>(
          <View key={i} style={st.teamCard}>
            <View style={{flexDirection:'row',alignItems:'center'}}>
              <View style={st.avatar}><Text style={st.avatarText}>{m.name.split(' ').map(n=>n[0]).join('').substring(0,2)}</Text></View>
              <View style={{flex:1,marginLeft:10}}>
                <Text style={st.teamName}>{m.name}</Text>
                <Text style={st.teamRole}>{m.position} · {m.department}</Text>
              </View>
              <View style={{alignItems:'flex-end'}}>
                <Text style={st.teamCount}>{m.assignedCount} leads</Text>
                <Text style={st.teamValue}>{formatCurrency(m.totalValue)}</Text>
              </View>
            </View>
            <View style={st.capacityBar}><View style={[st.capacityFill,{width:`${Math.min((m.assignedCount/m.capacity)*100,100)}%`,backgroundColor:m.assignedCount>=m.capacity?'#EF4444':m.assignedCount>=m.capacity*0.7?'#F59E0B':'#10B981'}]}/></View>
            <Text style={st.capacityText}>{m.available>0?`${m.available} slots available`:'At capacity'}</Text>
          </View>
        ))
      }
    </View>
  );

  const renderAssignModal=()=>(
    <Modal visible={showAssignModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowAssignModal(false)}>
      <View style={st.modalContainer}>
        <View style={st.modalHeader}>
          <Text style={st.modalTitle}>Assign: {selectedLead?.name}</Text>
          <TouchableOpacity style={st.modalClose} onPress={()=>setShowAssignModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity>
        </View>
        <ScrollView style={{flex:1,padding:16}}>
          <Text style={st.modalSub}>Select team member:</Text>
          {teamMembers.map((m,i)=>(
            <TouchableOpacity key={i} style={st.memberOption} onPress={()=>assignLead(selectedLead,m.id)}>
              <View style={st.avatar}><Text style={st.avatarText}>{m.name.split(' ').map(n=>n[0]).join('').substring(0,2)}</Text></View>
              <View style={{flex:1,marginLeft:10}}>
                <Text style={st.memberName}>{m.name}</Text>
                <Text style={st.memberMeta}>{m.position} · {m.assignedCount} leads · {formatCurrency(m.totalValue)}</Text>
              </View>
              <View style={[st.availBadge,{backgroundColor:m.available>3?'#ECFDF5':m.available>0?'#FFFBEB':'#FEF2F2'}]}>
                <Text style={[st.availText,{color:m.available>3?'#059669':m.available>0?'#D97706':'#DC2626'}]}>{m.available} free</Text>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    </Modal>
  );

  const renderContent=()=>{
    const wrap=ch=><ScrollView style={{flex:1}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#B45309']}/>} showsVerticalScrollIndicator={false}>{ch}<View style={{height:40}}/></ScrollView>;
    switch(activeTab){case 'assignments':return wrap(renderUnassigned());case 'assigned':return wrap(renderAssigned());case 'team':return wrap(renderTeam());default:return null;}
  };

  if(isLoading)return <SafeAreaView style={st.loading}><StatusBar barStyle="dark-content"/><ActivityIndicator size="large" color="#B45309"/><Text style={st.loadingText}>Loading assignments...</Text></SafeAreaView>;

  return (
    <SafeAreaView style={st.container}><StatusBar barStyle="light-content"/>
      {renderHeader()}{renderStats()}{renderTabs()}{renderContent()}{renderAssignModal()}
    </SafeAreaView>
  );
}

const st=StyleSheet.create({
  container:{flex:1,backgroundColor:'#F3F4F6'},loading:{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F3F4F6'},loadingText:{fontSize:16,color:'#6B7280',marginTop:16},
  header:{paddingTop:8,paddingBottom:16,paddingHorizontal:16},headerRow:{flexDirection:'row',alignItems:'center'},headerBtn:{width:40,height:40,borderRadius:20,backgroundColor:'rgba(255,255,255,0.12)',justifyContent:'center',alignItems:'center'},headerCenter:{flex:1,marginLeft:12},headerTitle:{fontSize:20,fontWeight:'bold',color:'#FFF'},headerSub:{fontSize:13,color:'#FDE68A',marginTop:2},
  statsRow:{flexDirection:'row',backgroundColor:'#FFF',padding:12,borderBottomWidth:1,borderBottomColor:'#E5E7EB',justifyContent:'space-around'},statCard:{alignItems:'center'},statValue:{fontSize:20,fontWeight:'bold'},statLabel:{fontSize:11,color:'#6B7280',marginTop:2},
  tabsRow:{backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E5E7EB'},tab:{flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:10,marginRight:4,borderRadius:20},activeTab:{backgroundColor:'#FFFBEB'},tabText:{fontSize:13,color:'#6B7280',marginLeft:6,fontWeight:'500'},tabBadge:{backgroundColor:'#EF4444',borderRadius:10,paddingHorizontal:6,paddingVertical:1,marginLeft:6},tabBadgeText:{color:'#FFF',fontSize:10,fontWeight:'bold'},
  section:{backgroundColor:'#FFF',margin:16,padding:16,borderRadius:12,borderWidth:1,borderColor:'#E5E7EB'},sectionTitle:{fontSize:17,fontWeight:'bold',color:'#1F2937',marginBottom:12},
  empty:{alignItems:'center',padding:32},emptyText:{fontSize:16,color:'#6B7280',marginTop:12},
  leadCard:{backgroundColor:'#FFF',marginBottom:8,padding:14,borderRadius:10,borderWidth:1,borderColor:'#E5E7EB'},leadName:{fontSize:15,fontWeight:'600',color:'#1F2937'},leadMeta:{fontSize:12,color:'#6B7280',marginTop:1},leadValue:{fontSize:14,fontWeight:'700',color:'#10B981',marginTop:2},
  assignBtn:{flexDirection:'row',alignItems:'center',backgroundColor:'#FFFBEB',borderRadius:8,paddingHorizontal:12,paddingVertical:8,borderWidth:1,borderColor:'#FDE68A'},assignBtnText:{color:'#B45309',fontWeight:'600',marginLeft:4,fontSize:13},
  assignedBadge:{flexDirection:'row',alignItems:'center',marginTop:4},assignedName:{color:'#10B981',fontSize:12,fontWeight:'500',marginLeft:4},
  teamCard:{backgroundColor:'#F9FAFB',borderRadius:10,padding:14,marginBottom:8},
  avatar:{width:36,height:36,borderRadius:18,backgroundColor:'#B45309',justifyContent:'center',alignItems:'center'},avatarText:{color:'#FFF',fontWeight:'bold',fontSize:13},
  teamName:{fontSize:15,fontWeight:'600',color:'#1F2937'},teamRole:{fontSize:12,color:'#6B7280'},teamCount:{fontSize:14,fontWeight:'600',color:'#1F2937'},teamValue:{fontSize:12,color:'#10B981',fontWeight:'500'},
  capacityBar:{height:4,backgroundColor:'#E5E7EB',borderRadius:2,marginTop:8,overflow:'hidden'},capacityFill:{height:'100%',borderRadius:2},capacityText:{fontSize:11,color:'#6B7280',marginTop:4},
  modalContainer:{flex:1,backgroundColor:'#FFF'},modalHeader:{flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},modalTitle:{fontSize:18,fontWeight:'bold',color:'#1F2937',flex:1},modalClose:{width:40,height:40,borderRadius:20,backgroundColor:'#F3F4F6',justifyContent:'center',alignItems:'center'},modalSub:{fontSize:14,color:'#6B7280',marginBottom:12},
  memberOption:{flexDirection:'row',alignItems:'center',padding:14,borderRadius:10,backgroundColor:'#F9FAFB',marginBottom:8},memberName:{fontSize:15,fontWeight:'600',color:'#1F2937'},memberMeta:{fontSize:12,color:'#6B7280',marginTop:1},
  availBadge:{borderRadius:12,paddingHorizontal:8,paddingVertical:4},availText:{fontSize:11,fontWeight:'600'},
});